// gridRefs.js - THE SAME READER AS back/utils/gridRefs.js (kept in step by
// src/helpers/__tests__/gridRefs.test.js, which runs both on the same cases).
// The picker splits and tidies grid locations with it, so what a person sees
// in the box is what the server stores and keys.

const LTR = "[A-Z]{1,2}(?:\\.\\d+)?'?";
const NUM = "\\d+(?:\\.\\d+)?'?";
// Between two lines of the SAME axis: "57-58", "41/46", "41 to 44", "G/H".
const DASH = "\\s*(?:[-\\u2013\\u2014/]|\\bTO\\b|\\bTHRU\\b)\\s*";
// Between the two axes: a dash, a slash, "@", ":", " x ", or nothing at all.
const SEP = "(?:\\s*[-\\u2013\\u2014/@:\\u00d7]\\s*|\\s+X\\s+|\\s+|)";

const RX_LN = new RegExp(`^(${LTR})(?:${DASH}(${LTR}))?${SEP}(${NUM})(?:${DASH}(${NUM}))?$`);
const RX_NL = new RegExp(`^(${NUM})(?:${DASH}(${NUM}))?${SEP}(${LTR})(?:${DASH}(${LTR}))?$`);
const RX_L = new RegExp(`^(${LTR})(?:${DASH}(${LTR}))?$`);
const RX_N = new RegExp(`^(${NUM})(?:${DASH}(${NUM}))?$`);

const MAX_WALK = 60;     // lines walked along one axis
const MAX_KEYS = 400;    // keys one reference may produce

const tidy = (s) => String(s == null ? "" : s).replace(/\s+/g, " ").trim().toUpperCase();
// "GL 45-I", "Grid K/57", "Gridlines A-C": the word in front is not a line.
const PREFIX = /^(?:GRID\s*LINES?|GRIDS?|GL|G\/L)\s*[:.]?\s+(?=\S)/;

function walkLetters(a, b) {
  if (!b || a === b) return [a];
  if (/^[A-Z]$/.test(a) && /^[A-Z]$/.test(b)) {
    const [x, y] = [a.charCodeAt(0), b.charCodeAt(0)].sort((p, q) => p - q);
    const out = [];
    for (let c = x; c <= y; c += 1) out.push(String.fromCharCode(c));
    return out;
  }
  return [a, b];
}
function walkNumbers(a, b) {
  if (!b || a === b) return [a];
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
    const [x, y] = [Number(a), Number(b)].sort((p, q) => p - q);
    if (y - x <= MAX_WALK) {
      const out = [];
      for (let n = x; n <= y; n += 1) out.push(String(n));
      return out;
    }
  }
  return [a, b];
}
// "057" and "57" are the same line; "C.40" and "C.4" are not touched.
const num = (n) => (/^\d+$/.test(n) ? String(Number(n)) : n);

/**
 * One reference -> { text, letters, numbers, keys } or null when it is not a
 * grid reference. `keys` holds the intersections ("I/40") and every line it
 * stands on ("I", "40"), so a filter on a line finds the intersections on it.
 */
export function parseGridRef(raw) {
  const text = tidy(raw);
  if (!text) return null;
  const body = text.replace(PREFIX, "");
  let m = RX_LN.exec(body);
  let letters; let numbers;
  if (m) { letters = walkLetters(m[1], m[2]); numbers = walkNumbers(num(m[3]), m[4] && num(m[4])); }
  else if ((m = RX_NL.exec(body))) { numbers = walkNumbers(num(m[1]), m[2] && num(m[2])); letters = walkLetters(m[3], m[4]); }
  else if ((m = RX_L.exec(body))) { letters = walkLetters(m[1], m[2]); numbers = []; }
  else if ((m = RX_N.exec(body))) { numbers = walkNumbers(num(m[1]), m[2] && num(m[2])); letters = []; }
  else return null;
  const keys = new Set([...letters, ...numbers]);
  if (letters.length && numbers.length) {
    // A huge range keeps its corners rather than thousands of keys.
    const ls = letters.length * numbers.length > MAX_KEYS ? [letters[0], letters[letters.length - 1]] : letters;
    const ns = letters.length * numbers.length > MAX_KEYS ? [numbers[0], numbers[numbers.length - 1]] : numbers;
    for (const l of ls) for (const n of ns) keys.add(`${l}/${n}`);
  }
  return { text, letters, numbers, intersection: !!(letters.length && numbers.length), keys: [...keys] };
}

/**
 * The field's text -> its references, in order, each as written.
 * Parts that are not a grid reference are kept as text (`parsed: false`), so
 * nothing anybody typed is dropped.
 */
export function splitGridRefs(raw) {
  // Free text keeps its own case ("by the stair"); a reference is upper case.
  const hard = String(raw == null ? "" : raw)
    .split(/[,;&\n\r]+|\s+AND\s+/i)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  const out = [];
  for (const part of hard) {
    const one = parseGridRef(part);
    if (one) { out.push({ ...one, parsed: true }); continue; }
    // "I-40 L-50" or "J 54 K 57 K 58": spaces separate when every piece is a
    // reference - two neighbours that make an intersection ("J 54") first.
    const pieces = tidy(part).replace(PREFIX, "").split(" ").filter(Boolean);
    const each = [];
    for (let i = 0; i < pieces.length; i += 1) {
      const two = i + 1 < pieces.length ? parseGridRef(`${pieces[i]} ${pieces[i + 1]}`) : null;
      if (two && two.intersection) { each.push(two); i += 1; continue; }
      each.push(parseGridRef(pieces[i]));
    }
    if (pieces.length > 1 && each.every(Boolean)) { each.forEach((r) => out.push({ ...r, parsed: true })); continue; }
    out.push({ text: part, letters: [], numbers: [], intersection: false, keys: [], parsed: false });
  }
  // The same place written twice is one reference.
  const seen = new Set();
  return out.filter((r) => {
    const k = r.parsed ? r.keys.slice().sort().join("|") : `t:${r.text}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** The list of references -> the one stored string ("I-40, L-50"). */
export function joinGridRefs(list) {
  return (list || []).map((r) => (typeof r === "string" ? tidy(r) : r.text)).filter(Boolean).join(", ");
}

/** The stored string, tidied: split, de-duplicated, joined. */
export function normaliseGrid(raw) {
  return joinGridRefs(splitGridRefs(raw));
}

/** Every key the field's references stand on - what location.gridRefs holds. */
export function gridKeys(raw) {
  const keys = new Set();
  for (const r of splitGridRefs(raw)) r.keys.forEach((k) => keys.add(k));
  return [...keys];
}

/**
 * What a FILTER asks for: an intersection matches its own keys only (I-40 is
 * not "anywhere on line I"); a line matches everything on it.
 */
export function filterKeys(raw) {
  const keys = new Set();
  const unparsed = [];
  for (const r of splitGridRefs(raw)) {
    if (!r.parsed) { unparsed.push(r.text); continue; }
    if (r.intersection) r.keys.filter((k) => k.includes("/")).forEach((k) => keys.add(k));
    else r.keys.forEach((k) => keys.add(k));
  }
  return { keys: [...keys], unparsed };
}
